import { Component, Injector, OnInit, OnChanges } from '@angular/core';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { FormStatusEnum, WFSaveOptions } from 'src/app/common/enumerations/enumerations';
import { PlaceholdersService } from 'src/app/services/placeholders/placeholders.service';

@Component({
  selector: 'cl-radio-placeholder-cond',
  templateUrl: './cl-radio-placeholder-cond.component.html',
  styleUrls: ['./cl-radio-placeholder-cond.component.scss']
})
export class ClRadioPlaceholderCondComponent extends ClBaseComponent implements OnInit {

  constructor(
    protected override injector: Injector,
    private placeholderService: PlaceholdersService
  ) {
    super(injector);
  }

  /**
   * Evaluates placeholder conditions while preserving completed selections during ordinary refreshes.
   */
  ngOnInit() {
    if (!this.editor && !this.builder && this.checklist?.checklistStatus?.formStatusID != FormStatusEnum.Canceled) {
      this.placeholderService.keyValuePairsObservable.subscribe(data => {
        // Keep the completed selection stable during incoming-monitor and checklist refreshes.
        // A suggested selection is also stable until the monitor step explicitly requests a
        // condition recalculation; otherwise the BehaviorSubject's initial emission clears the
        // bulb before the real placeholder values arrive.
        if (this.radioSelection && !this.placeholderService.recalculateRadMonitorConditions) this.setValues();
        else this.placeHolderSelection();
      });
    }
  }

  setValues() {
    const configuration = this.utils.JSONparse(this.configuration);
    this.radioOptions?.map(o => o.checked = o.id == this.radioSelection);
  }

  /**
   * Derives the radio and checkbox state from the configured numeric placeholder.
   */
  placeHolderSelection() {
    const configuration = this.utils.JSONparse(this.configuration);
    const placeholder = configuration?.placeholder;
    const value = this.placeholderService.getValue(placeholder);
    if (!isNaN(value) && value !== null && value !== '') {
      const manuallySigned = this.checked && this.user != null;
      let selectedOptionId: number | undefined;
      this.radioOptions?.map(o => {
        if (o.operator) {
          const result = this.utils.compareNumbers(value, o.value, o.operator);
          if (result) {
            selectedOptionId = o.id;
          }
          o.checked = result;
        }
      });

      const hasSelection = !this.unselected();
      if (hasSelection && selectedOptionId != null) {
        // A placeholder result is a recommendation, not an operator signature. Persist the
        // derived radio value as automatic so the parent does not route it through manual signing.
        if (!manuallySigned && this.radioSelection != selectedOptionId) {
          this.radioSelected.emit({ value: selectedOptionId, option: WFSaveOptions.AUTO });
        }
        this.radioSelection = selectedOptionId;
        this.checked = manuallySigned;
        this.suggested = !manuallySigned;
        this.suggestionReason = this.suggested
          ? 'The tripset value was calculated from the selected monitor. Review the value and sign this task manually.'
          : null;
      }
      else if (!manuallySigned) {
        this.radioSelection = 0;
        this.checked = false;
        this.suggested = false;
        this.suggestionReason = null;
      }
    }
    // Missing values can be caused by the shared placeholder stream being initialized or by an
    // unrelated resource update. Keep the persisted recommendation intact until a real monitor
    // recalculation supplies a replacement value.
  }

  /**
   * Applies a checkbox change only when one radio option is selected.
   * @param e - Checkbox event containing the requested checked state.
   */
  checkboxChanged(e: any) {
    // Never allow the task checkbox to complete without a derived or user-selected radio option.
    if (e.checked && this.unselected()) {
      e.source.checked = false;
      return;
    }
    this.checked = e.checked;
    this.answered.emit(this.checked);
  }

  /**
   * Applies a radio option selected directly by the operator and starts the manual save path.
   * @param e Material radio change event containing the selected option id.
   */
  radioChange(e: any) {
    this.radioSelected.emit(e.value);
    this.radioSelection = e.value;
    this.checked = true;
    this.radioOptions?.map(x => x.checked = x.id == this.radioSelection);
  }

  /**
   * Treats a persisted radio selection as selected while option checked flags are being rebuilt.
   * @returns True when no radio option is selected for the task.
   */
  unselected() {
    return !this.radioOptions?.some(r => r.checked || r.id == this.radioSelection);
  }

  setRadio(value: any) {
    this.radioOptions?.map(r => {
      r.checked = r.value == value;
    });
  }

}
